import {
  DataBlockVariant,
  DeviceModel,
  DictionaryRecordDto,
  QueryFactory,
  useEnabledDataBlockTypes,
} from '@lmt/shared-types';
import { useDataBlockTypeTranslation } from '@lmt/unicorn-shared/app/components/measurements/common';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { deviceModels } from '@lmt/unicorn-shared/app/features/mappings/operations';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import Logger from 'js-logger';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useUniDispatch } from '../../store/types';
import { Field } from '../common/Field';
import { Loading } from '../common/Loading';
import { Button, ButtonColor } from '../common/ui-kit/buttons/Button';
import { DropDownInput } from '../common/ui-kit/inputs/dropdown/DropDownInput';
import { useGlobalForms } from '../global-forms/useGlobalForms';
import { MappingsTable } from './components/MappingsTable';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

import styles from './MappingsPage.module.scss';
import { useClaims } from '../../features/auth/operations';

export const MappingsPage: FC = React.memo(function MappingsPage() {
  const { deviceId, measurementTypeId } =
    pages.mappingsWithDeviceAndMeasurement.useMatch()?.params ?? {
      deviceId: undefined,
      measurementTypeId: undefined,
    };
  const tabs = useTabs();
  const i18next = useScopedTranslation('mappings');
  const [isLoading, setLoading] = useState<boolean>(false);
  const [selectedDeviceModel, setDeviceModel] = useState<
    DeviceModel | undefined
  >();
  const [dataBlockTypeId, setDataBlockTypeId] = useState<number | null>(null);
  const dataBlockTypes = useEnabledDataBlockTypes();
  const translateDataBlockType = useDataBlockTypeTranslation();
  const forms = useGlobalForms();
  const dispatch = useUniDispatch();
  const modals = useModal();
  const claims = useClaims();

  const filteredDataBlockTypes = useMemo(() => {
    return dataBlockTypes.filter(
      (type) => type.variant === DataBlockVariant.DeviceMeasurement,
    );
  }, [dataBlockTypes]);

  const reImportAllMeasurements = useCallback(async () => {
    const result = await modals.showConfirm({
      title: i18next.t('confirmations.re_import_measurements.title'),
      text: i18next.t('confirmations.re_import_measurements.text'),
      okButtonText: i18next.t('confirmations.re_import_measurements.ok_button'),
    });
    if (result) {
      const processId = await QueryFactory.DataBlockClient.startReimport();
      forms.openReImportMeasurements(processId);
    }
  }, [dispatch]);

  const updateDeviceModel = useCallback(() => {
    const deviceModelToSelect = deviceModels.find(
      (device) => device.value === deviceId,
    )?.value;
    if (deviceModelToSelect) {
      setDeviceModel(deviceModelToSelect);
    } else {
      const path = pages.mappingsWithDeviceAndMeasurement.link({
        deviceId: deviceModels[0].value,
        measurementTypeId: measurementTypeId || filteredDataBlockTypes[0].id,
      });
      tabs.changeTabPath(path);
    }
  }, [deviceId]);

  const updateSelectedMeasurementType = useCallback(async () => {
    if (filteredDataBlockTypes.length > 0) {
      const measurementTypeToSelect = filteredDataBlockTypes.find(
        (type) => type.id === measurementTypeId,
      );
      if (measurementTypeToSelect) {
        setDataBlockTypeId(measurementTypeToSelect.id);
      } else {
        const path = pages.mappingsWithDeviceAndMeasurement.link({
          deviceId: deviceId || deviceModels[0].value,
          measurementTypeId: filteredDataBlockTypes[0].id,
        });
        tabs.changeTabPath(path);
      }
    }
  }, [filteredDataBlockTypes, measurementTypeId]);

  useEffect(() => {
    updateDeviceModel();
  }, [deviceId]);

  useEffect(() => {
    updateSelectedMeasurementType().catch((e) => Logger.error(e));
  }, [filteredDataBlockTypes, measurementTypeId]);

  const openMeasurementFields = useCallback(
    (measurementTypeId: number | undefined) => {
      tabs.openTab(
        pages.dataBlockFields.link({
          variant: DataBlockVariant.DeviceMeasurement,
          typeId: measurementTypeId ?? '',
        }),
      );
    },
    [],
  );

  const onMeasurementTypeChanged = useCallback(
    (measurementType: Partial<DictionaryRecordDto>) => {
      if (measurementTypeId !== measurementType.id) {
        const path = pages.mappingsWithDeviceAndMeasurement.link({
          deviceId: selectedDeviceModel!,
          measurementTypeId: measurementType.id,
        });
        tabs.changeTabPath(path);
      }
    },
    [selectedDeviceModel, measurementTypeId],
  );

  const onDeviceModelChanged = useCallback(
    (deviceModel: DeviceModel) => {
      if (deviceId !== deviceModel) {
        const path = pages.mappingsWithDeviceAndMeasurement.link({
          deviceId: deviceModel,
          measurementTypeId:
            dataBlockTypeId! /*ToDo: fix this after react-router 6.14*/,
        });
        tabs.changeTabPath(path);
      }
    },
    [dataBlockTypeId, deviceId],
  );

  const selectDeviceModel = (
    <Field title={i18next.t('device')} className={styles.filterField}>
      <DropDownInput
        useIdFunctionAsValue={true}
        required
        variant="normal"
        getOptionLabel="displayValue"
        idFunction="value"
        options={deviceModels}
        value={selectedDeviceModel || null}
        onValueChanged={(option) => {
          onDeviceModelChanged(option.value);
        }}
      />
    </Field>
  );

  const selectMeasurementType = dataBlockTypeId && (
    <Field
      title={i18next.t('measurement_type')}
      className={styles.filterField}
      linkProps={{
        title: i18next.t('add_type_button'),
        onClick: () =>
          forms.openDataBlockTypeCreation(
            DataBlockVariant.DeviceMeasurement,
            (newDataBlockTypeId) => {
              setDataBlockTypeId(newDataBlockTypeId);
            },
          ),
        className: styles.edit,
      }}
    >
      <DropDownInput
        useIdFunctionAsValue={true}
        required
        variant="normal"
        getOptionLabel={translateDataBlockType}
        idFunction="id"
        options={filteredDataBlockTypes}
        value={dataBlockTypeId}
        onValueChanged={(option) => {
          onMeasurementTypeChanged(option);
        }}
      />
    </Field>
  );

  return (
    <div className={styles.storedScrolling}>
      <Loading
        title={i18next.t('loading')}
        loading={isLoading}
        testId="data-block-mappings-page"
      >
        <div className={styles.container}>
          <div className={styles.filterArea}>
            {selectDeviceModel}
            {selectMeasurementType}
            {claims.permissions.editDataBlockTypesAndFields && (
              <Button
                title={i18next.t('data_block_fields')}
                onClick={() =>
                  openMeasurementFields(dataBlockTypeId ?? undefined)
                }
              />
            )}
            {claims.permissions.reImportAllMeasurements && (
              <Button
                title={i18next.t('re_import_all_measurements')}
                onClick={reImportAllMeasurements}
                color={ButtonColor.Primary}
                className={styles.button}
              />
            )}
          </div>
          <div className={styles.table}>
            {selectedDeviceModel && dataBlockTypeId && (
              <MappingsTable
                measurementTypeId={dataBlockTypeId}
                deviceModel={selectedDeviceModel}
              />
            )}
          </div>
        </div>
      </Loading>
    </div>
  );
});
